import axios from 'axios';

// Use relative URL for same-origin deployment, or env variable for cross-origin dev
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Helper to get full URL for API paths (useful for image URLs from GridFS)
export const getFullApiUrl = (path) => {
  if (!path) return path;
  // If already a full URL or data URL, return as-is
  if (path.startsWith('http') || path.startsWith('data:')) return path;
  // If it's an API path, prepend the backend URL
  if (path.startsWith('/api')) return `${BACKEND_URL}${path}`;
  return path;
};

// Create axios instance with credentials
const axiosInstance = axios.create({
  withCredentials: true,
  timeout: 30000 // 30 second default timeout
});

// Create a separate instance for long-running operations
const longRunningAxios = axios.create({
  withCredentials: true,
  timeout: 300000 // 5 minute timeout for content generation (complex photos may take longer)
});

// Add Authorization header fallback for devices where cookies don't work (iPad Safari)
const addAuthHeader = (config) => {
  const token = localStorage.getItem('firstcut_session_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
};
axiosInstance.interceptors.request.use(addAuthHeader);
longRunningAxios.interceptors.request.use(addAuthHeader);

// Handle AI-specific errors
const handleAIError = (error) => {
  const data = error.response?.data;
  
  // Check if it's an AI generation or parse error
  if (data?.type === 'ai_generation_error' || data?.type === 'ai_parse_error') {
    // Create a user-friendly error with retry capability
    const enhancedError = new Error(data.user_message || data.error);
    enhancedError.isAIError = true;
    enhancedError.stage = data.stage;
    enhancedError.canRetry = data.retry !== false;
    enhancedError.originalError = error;
    return Promise.reject(enhancedError);
  }
  
  return Promise.reject(error);
};

// Add response interceptor for auth errors
const handleAuthError = (error) => {
  if (error.response?.status === 401) {
    // Check if user just authenticated - don't redirect immediately
    const justAuthenticated = sessionStorage.getItem('just_authenticated');
    const loginTime = sessionStorage.getItem('login_time');
    const timeSinceLogin = loginTime ? (Date.now() - parseInt(loginTime)) : Infinity;
    
    // If we just logged in (within last 10 seconds), don't redirect
    // This prevents race conditions where cookie hasn't propagated yet
    if (justAuthenticated || timeSinceLogin < 10000) {
      console.log('Ignoring 401 - just authenticated, cookie may not have propagated yet');
      // Don't clear storage or redirect, just reject the error
      const authError = new Error('Session verification pending...');
      authError.isAuthError = true;
      authError.shouldRedirect = false;
      return Promise.reject(authError);
    }
    
    // Session truly expired - clear local storage
    localStorage.removeItem('firstcut_user');
    localStorage.removeItem('firstcut_session');
    sessionStorage.removeItem('just_authenticated');
    sessionStorage.removeItem('login_time');
    
    // Create a user-friendly error before redirecting
    const authError = new Error('Your session has expired. Please sign in again.');
    authError.isAuthError = true;
    authError.shouldRedirect = true;
    
    // Only redirect if not already on landing page
    if (window.location.pathname !== '/') {
      // Small delay to allow error to be displayed briefly
      setTimeout(() => {
        window.location.href = '/?session_expired=true';
      }, 100);
    }
    
    return Promise.reject(authError);
  }
  // Check for AI errors before rejecting
  return handleAIError(error);
};

axiosInstance.interceptors.response.use(
  response => response,
  handleAuthError
);

longRunningAxios.interceptors.response.use(
  response => response,
  handleAuthError
);

// Helper to get user_id from stored user or return null
const getUserId = () => {
  // Try to get from localStorage as fallback
  const storedUser = localStorage.getItem('firstcut_user');
  if (storedUser) {
    try {
      return JSON.parse(storedUser).user_id;
    } catch {
      return null;
    }
  }
  return null;
};

export const api = {
  // Health Check
  checkAIHealth: async () => {
    try {
      const response = await axiosInstance.get(`${API}/health/ai`);
      return response.data;
    } catch (err) {
      // Return degraded status if health check fails
      return { status: 'degraded', message: 'Unable to check status' };
    }
  },

  // Auth
  getMe: async () => {
    const response = await axiosInstance.get(`${API}/auth/me`);
    return response.data;
  },

  createSession: async (sessionData) => {
    const response = await axiosInstance.post(`${API}/auth/session`, sessionData);
    // Store user for future requests
    if (response.data) {
      localStorage.setItem('firstcut_user', JSON.stringify(response.data));
    }
    return response.data;
  },

  logout: async () => {
    localStorage.removeItem('firstcut_user');
    const response = await axiosInstance.post(`${API}/auth/logout`);
    return response.data;
  },

  // User
  getUser: async () => {
    const response = await axiosInstance.get(`${API}/auth/me`);
    return response.data;
  },

  getUserUsage: async () => {
    const response = await axiosInstance.get(`${API}/user/usage`);
    return response.data;
  },

  updateUserPlan: async (tier) => {
    const response = await axiosInstance.post(`${API}/user/plan`, { tier });
    return response.data;
  },

  // Plans/Pricing
  getPlans: async () => {
    const response = await axiosInstance.get(`${API}/plans`);
    return response.data;
  },

  // Clients
  createClient: async (clientData) => {
    const response = await axiosInstance.post(`${API}/clients`, clientData);
    return response.data;
  },

  listClients: async () => {
    const response = await axiosInstance.get(`${API}/clients`);
    return response.data;
  },

  getClient: async (clientId) => {
    const response = await axiosInstance.get(`${API}/clients/${clientId}`);
    return response.data;
  },

  deleteClient: async (clientId) => {
    const response = await axiosInstance.delete(`${API}/clients/${clientId}`);
    return response.data;
  },

  restoreClient: async (clientId) => {
    const response = await axiosInstance.post(`${API}/clients/${clientId}/restore`);
    return response.data;
  },

  permanentlyDeleteClient: async (clientId) => {
    const response = await axiosInstance.delete(`${API}/clients/${clientId}/permanent`);
    return response.data;
  },

  listArchivedClients: async () => {
    const response = await axiosInstance.get(`${API}/clients/archived`);
    return response.data;
  },

  getClientInsights: async (clientId) => {
    const response = await axiosInstance.get(`${API}/clients/${clientId}/insights`);
    return response.data;
  },

  updateClientProductImages: async (clientId, productImages) => {
    const response = await axiosInstance.put(`${API}/clients/${clientId}/product-images`, {
      product_images: productImages
    });
    return response.data;
  },

  analyzeProductImage: async (clientId, imageBase64) => {
    const response = await longRunningAxios.post(`${API}/clients/${clientId}/analyze-product`, {
      image_base64: imageBase64
    });
    return response.data;
  },

  // Brand Assets
  getBrandAssets: async (clientId) => {
    const response = await axiosInstance.get(`${API}/clients/${clientId}/brand-assets`);
    return response.data;
  },

  addBrandAsset: async (clientId, assetData) => {
    const response = await longRunningAxios.post(`${API}/clients/${clientId}/brand-assets`, assetData);
    return response.data;
  },

  deleteBrandAsset: async (clientId, assetId) => {
    const response = await axiosInstance.delete(`${API}/clients/${clientId}/brand-assets/${assetId}`);
    return response.data;
  },

  updateBrandAsset: async (clientId, assetId, updates) => {
    const response = await axiosInstance.put(`${API}/clients/${clientId}/brand-assets/${assetId}`, updates);
    return response.data;
  },

  // ===== V2 CLEAN CONTENT SERVICES =====
  // These use the new architecture with literal photo constraints
  
  analyzePhotoV2: async (data) => {
    // Returns literal description with visible_elements and not_visible constraints
    const response = await longRunningAxios.post(`${API}/v2/content/analyze-photo`, data);
    return response.data;
  },

  generateFocusOptionsV2: async (photoContext, options = {}) => {
    // Uses literal photo description as constraint
    const response = await longRunningAxios.post(`${API}/v2/focus/generate-options`, {
      photo_context: photoContext,
      num_options: options.num_options || 3,
      avoid_options: options.avoid_options || [],
      custom_hint: options.custom_hint,
      client_id: options.client_id,
      client_name: options.client_name,
      client_industry: options.client_industry,
      brand_voice: options.brand_voice,
      target_audience: options.target_audience,
      campaign_id: options.campaign_id,
      campaign_name: options.campaign_name,
      creative_angle: options.creative_angle
    });
    return response.data;
  },

  // Generate focus options without a photo (client + campaign context only)
  generateFocusOptionsNoPhoto: async (options = {}) => {
    const response = await longRunningAxios.post(`${API}/v2/content/generate-focus-no-photo`, {
      client_id: options.client_id,
      campaign_id: options.campaign_id,
      num_options: options.num_options || 3,
      avoid_options: options.avoid_options || []
    });
    return response.data;
  },

  generateContentV2: async (photoContext, focusOption, options = {}) => {
    // Generates content constrained by photo analysis
    const response = await longRunningAxios.post(`${API}/v2/content/generate`, {
      photo_context: photoContext,
      focus_option: focusOption,
      platform: options.platform || 'instagram_post',
      content_type: options.content_type || 'instagram_post',
      count: options.count || 1,
      additional_brief: options.additional_brief,
      generate_images: options.generate_images !== false,
      image_options: options.image_options || {},
      client_id: options.client_id,
      client_name: options.client_name,
      client_industry: options.client_industry,
      brand_voice: options.brand_voice,
      target_audience: options.target_audience,
      campaign_id: options.campaign_id,
      campaign_name: options.campaign_name,
      creative_angle: options.creative_angle,
      brand_colors: options.brand_colors
    });
    return response.data;
  },

  // ===== CLIENT PHOTOS (Recent Photos Gallery) =====
  getClientRecentPhotos: async (clientId) => {
    const response = await axiosInstance.get(`${API}/clients/${clientId}/recent-photos`);
    return response.data;
  },

  saveClientPhoto: async (clientId, photoData) => {
    // photoData: { url, name, context_mode, analysis_summary }
    const response = await axiosInstance.post(`${API}/clients/${clientId}/photos`, photoData);
    return response.data;
  },

  // Campaigns
  generateCampaignSuggestions: async (data) => {
    // data can include: client_id, objective, custom_direction, user_edits, 
    // previous_suggestions (for avoiding repeats), is_regeneration (for charging credits)
    // Use longRunningAxios for AI operations that can take 30-60 seconds
    const response = await longRunningAxios.post(`${API}/campaigns/generate-suggestions`, data);
    return response.data;
  },

  generateCampaignNames: async (campaignData) => {
    const response = await longRunningAxios.post(`${API}/campaigns/generate-names`, campaignData);
    return response.data;
  },

  generateCreativeAngles: async (campaignData) => {
    const response = await longRunningAxios.post(`${API}/campaigns/generate-angles`, campaignData);
    return response.data;
  },

  getContentGoals: async () => {
    const response = await axiosInstance.get(`${API}/content-goals`);
    return response.data;
  },

  createCampaign: async (campaignData) => {
    const response = await axiosInstance.post(`${API}/campaigns`, null, {
      params: campaignData
    });
    return response.data;
  },

  listCampaigns: async (clientId) => {
    const response = await axiosInstance.get(`${API}/campaigns`, {
      params: { client_id: clientId }
    });
    return response.data;
  },

  getCampaign: async (campaignId) => {
    const response = await axiosInstance.get(`${API}/campaigns/${campaignId}`);
    return response.data;
  },

  // Content Focus - LEGACY REMOVED, use V2 endpoints above
  // generateFocusOptions → Use generateFocusOptionsV2

  createContentFocus: async (focusData, selectedOption) => {
    const params = {};
    if (selectedOption) {
      params.selected_option = JSON.stringify(selectedOption);
    }
    // Use longer timeout - this can involve AI analysis of reference images
    const response = await longRunningAxios.post(`${API}/focus`, focusData, { params });
    return response.data;
  },

  generateCustomFocusDetails: async (campaignId, briefDescription, productContext = null) => {
    // Use longer timeout for AI generation
    const response = await longRunningAxios.post(`${API}/focus/generate-custom`, {
      campaign_id: campaignId,
      brief_description: briefDescription,
      product_context: productContext
    });
    return response.data;
  },

  // Suggest overlay text using AI
  suggestOverlayText: async (context) => {
    const response = await axiosInstance.post(`${API}/content/suggest-overlay-text`, context);
    return response.data;
  },

  listContentFocuses: async (campaignId) => {
    const response = await axiosInstance.get(`${API}/focus`, {
      params: { campaign_id: campaignId }
    });
    return response.data;
  },

  // Content Generation
  generateContent: async (request) => {
    // Use long-running axios for content generation (can take 1-2 mins)
    const response = await longRunningAxios.post(`${API}/content/generate`, request);
    return response.data;
  },

  // Generate More Content - Reuse existing focus with fresh angles
  generateMoreContent: async (request) => {
    // Use long-running axios for content generation (can take 1-2 mins)
    const response = await longRunningAxios.post(`${API}/content/generate-more`, request);
    return response.data;
  },

  listContentIdeas: async (filters = {}, page = 1, limit = 20) => {
    const params = new URLSearchParams({ ...filters, page: String(page), limit: String(limit) });
    const url = `${API}/content?${params.toString()}`;
    
    const token = localStorage.getItem('firstcut_session_token');
    const headers = { 'Accept': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const response = await fetch(url, {
      credentials: 'include',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  },

  // Get image for a specific content idea (lazy loading)
  // If retry=true and image is missing, backend will attempt to regenerate it
  // If allImages=true, returns all images for ideas with multiple images
  getContentImage: async (ideaId, retry = false, allImages = false) => {
    const response = await (retry ? longRunningAxios : axiosInstance).get(`${API}/content/${ideaId}/image`, {
      params: { retry, all_images: allImages }
    });
    return response.data;
  },

  updateContentStatus: async (ideaId, status) => {
    const response = await axiosInstance.put(`${API}/content/${ideaId}/status`, null, {
      params: { status }
    });
    return response.data;
  },

  // Apply logo to content with user-specified settings
  applyLogo: async (ideaId, settings) => {
    const response = await axiosInstance.post(`${API}/content/${ideaId}/apply-logo`, settings);
    return response.data;
  },

  // Skip logo for content (user chose not to add logo)
  skipLogo: async (ideaId) => {
    const response = await axiosInstance.post(`${API}/content/${ideaId}/skip-logo`);
    return response.data;
  },

  // Remove logo from content and restore original image
  removeLogo: async (ideaId) => {
    const response = await axiosInstance.post(`${API}/content/${ideaId}/remove-logo`);
    return response.data;
  },

  // Generate video prompt from visual direction and camera movement
  generateVideoPrompt: async (data) => {
    const response = await axiosInstance.post(`${API}/content/video/generate-prompt`, data);
    return response.data;
  },

  regenerateImage: async (ideaId, options = {}) => {
    // Use long-running axios for image regeneration (can take 30+ seconds)
    const response = await longRunningAxios.post(`${API}/content/${ideaId}/regenerate-image`, null, {
      params: options
    });
    return response.data;
  },

  // Track content download - implicit positive signal for AI learning
  trackDownload: async (ideaId) => {
    try {
      const response = await axiosInstance.post(`${API}/content/${ideaId}/download`);
      return response.data;
    } catch (error) {
      // Don't fail silently - log but don't block download
      console.log('Download tracking failed:', error);
      return { success: false };
    }
  },

  // Track content copy - implicit positive signal for AI learning
  trackCopy: async (ideaId, field) => {
    try {
      const response = await axiosInstance.post(`${API}/content/${ideaId}/copy`, { field });
      return response.data;
    } catch (error) {
      // Don't fail silently - log but don't block copy
      console.log('Copy tracking failed:', error);
      return { success: false };
    }
  },

  // V3 Refine - Clean implementation (V1 and V2 removed)
  // Flow: Analyze generated image → Parse instruction → Validate → Generate
  refineContent: async (refinementData) => {
    // Clean 5-step refinement flow
    // Takes generated_image_base64, user_instruction
    // Returns refined_image_base64, interpretation, visual_dna
    const response = await longRunningAxios.post(`${API}/v3/content/refine`, refinementData);
    return response.data;
  },

  // Version History
  getContentVersions: async (ideaId) => {
    const response = await axiosInstance.get(`${API}/content/${ideaId}/versions`);
    return response.data;
  },

  restoreContentVersion: async (ideaId, versionNumber) => {
    const response = await axiosInstance.post(`${API}/content/${ideaId}/restore/${versionNumber}`);
    return response.data;
  },

  deleteContentVersion: async (ideaId, versionNumber) => {
    const response = await axiosInstance.delete(`${API}/content/${ideaId}/version/${versionNumber}`);
    return response.data;
  },

  // Settings
  updateNoveltyThreshold: async (threshold) => {
    const response = await axiosInstance.put(`${API}/settings/novelty-threshold`, null, {
      params: { threshold }
    });
    return response.data;
  },

  getNoveltyThreshold: async () => {
    const response = await axiosInstance.get(`${API}/settings/novelty-threshold`);
    return response.data;
  },

  // Moderation
  getModerationStats: async () => {
    const response = await axiosInstance.get(`${API}/moderation/stats`);
    return response.data;
  },

  getRecentBlocks: async (limit = 10) => {
    const response = await axiosInstance.get(`${API}/moderation/recent-blocks`, {
      params: { limit }
    });
    return response.data;
  },

  // Support
  submitSupportRequest: async (requestData) => {
    const response = await axiosInstance.post(`${API}/support/request`, requestData);
    return response.data;
  },

  getSupportTickets: async () => {
    const response = await axiosInstance.get(`${API}/support/tickets`);
    return response.data;
  },

  // Payments (Razorpay)
  createPaymentOrder: async (planId) => {
    const response = await axiosInstance.post(`${API}/payments/create-order`, null, {
      params: { plan_id: planId }
    });
    return response.data;
  },

  verifyPayment: async (paymentData) => {
    const response = await axiosInstance.post(`${API}/payments/verify`, paymentData);
    return response.data;
  },

  getPaymentStatus: async (orderId) => {
    const response = await axiosInstance.get(`${API}/payments/status/${orderId}`);
    return response.data;
  },

  getPaymentPlans: async () => {
    const response = await axiosInstance.get(`${API}/payments/plans`);
    return response.data;
  },

  // Credit Packs (one-time purchases, never expire)
  getCreditPacks: async () => {
    const response = await axiosInstance.get(`${API}/payments/credit-packs`);
    return response.data;
  },

  createPackOrder: async (packId) => {
    const response = await axiosInstance.post(`${API}/payments/create-pack-order`, null, {
      params: { pack_id: packId }
    });
    return response.data;
  },

  verifyPackPayment: async (paymentData) => {
    const response = await axiosInstance.post(`${API}/payments/verify-pack`, paymentData);
    return response.data;
  },

  // Subscriptions (recurring monthly)
  getSubscriptionPlans: async () => {
    const response = await axiosInstance.get(`${API}/payments/subscriptions`);
    return response.data;
  },

  getUserSubscription: async () => {
    const response = await axiosInstance.get(`${API}/payments/user-subscription`);
    return response.data;
  },

  createSubscription: async (planId) => {
    const response = await axiosInstance.post(`${API}/payments/create-subscription`, null, {
      params: { plan_id: planId }
    });
    return response.data;
  },

  verifySubscription: async (subscriptionData) => {
    const response = await axiosInstance.post(`${API}/payments/verify-subscription`, subscriptionData);
    return response.data;
  },

  cancelSubscription: async () => {
    const response = await axiosInstance.post(`${API}/payments/cancel-subscription`);
    return response.data;
  },

  // Demo Mode
  setupDemoData: async () => {
    const response = await axiosInstance.post(`${API}/demo/setup`);
    return response.data;
  },

  cleanupDemoData: async () => {
    const response = await axiosInstance.delete(`${API}/demo/cleanup`);
    return response.data;
  },

  getOnboardingStatus: async () => {
    const response = await axiosInstance.get(`${API}/user/onboarding-status`);
    return response.data;
  },

  markWelcomeSeen: async () => {
    const response = await axiosInstance.post(`${API}/user/mark-welcome-seen`);
    return response.data;
  },

  // ===== INTELLIGENCE & LEARNING ENDPOINTS =====
  
  getIntelligenceStatus: async () => {
    const response = await axiosInstance.get(`${API}/intelligence/status`);
    return response.data;
  },

  getClientLearningInsights: async (clientId) => {
    const response = await axiosInstance.get(`${API}/intelligence/learning/${clientId}`);
    return response.data;
  },

  prewarmIntelligence: async (clientId) => {
    const response = await axiosInstance.post(`${API}/intelligence/prewarm/${clientId}`);
    return response.data;
  },

  // ===== FOUNDER DASHBOARD =====
  getFounderStats: async () => {
    const response = await axiosInstance.get(`${API}/founder/stats`);
    return response.data;
  },

  getFounderTasks: async () => {
    const response = await axiosInstance.get(`${API}/founder/tasks`);
    return response.data;
  },

  createFounderTask: async (taskData) => {
    const response = await axiosInstance.post(`${API}/founder/tasks`, taskData);
    return response.data;
  },

  updateFounderTask: async (taskId, updates) => {
    const response = await axiosInstance.patch(`${API}/founder/tasks/${taskId}`, updates);
    return response.data;
  },

  deleteFounderTask: async (taskId) => {
    const response = await axiosInstance.delete(`${API}/founder/tasks/${taskId}`);
    return response.data;
  },

  getFounderAuditLogs: async (limit = 100, action = null) => {
    const params = { limit };
    if (action) params.action = action;
    const response = await axiosInstance.get(`${API}/founder/audit-logs`, { params });
    return response.data;
  },

  getFounderSecurityStats: async () => {
    const response = await axiosInstance.get(`${API}/founder/security-stats`);
    return response.data;
  },

  // ===== ISSUE REPORTING =====
  submitIssue: async (issueData) => {
    const response = await axiosInstance.post(`${API}/support/report-issue`, issueData);
    return response.data;
  },

  getIssues: async (status = null, limit = 50) => {
    const params = { limit };
    if (status) params.status = status;
    const response = await axiosInstance.get(`${API}/founder/issues`, { params });
    return response.data;
  },

  updateIssue: async (issueId, updates) => {
    const response = await axiosInstance.patch(`${API}/founder/issues/${issueId}`, updates);
    return response.data;
  },

  // ===== INDIA 2026 COMPLIANCE =====
  
  getComplianceSettings: async (clientId) => {
    const response = await axiosInstance.get(`${API}/compliance/settings/${clientId}`);
    return response.data;
  },

  saveComplianceSettings: async (clientId, settings) => {
    const response = await axiosInstance.post(`${API}/compliance/settings/${clientId}`, settings);
    return response.data;
  },

  classifyContentType: async (params) => {
    const response = await axiosInstance.get(`${API}/compliance/classify`, { params });
    return response.data;
  },

  generateComplianceMetadata: async (data) => {
    const response = await axiosInstance.post(`${API}/compliance/generate-metadata`, data);
    return response.data;
  },

  getExportDisclaimer: async (contentType, complianceLevel) => {
    const response = await axiosInstance.get(`${API}/compliance/export-disclaimer`, {
      params: { content_type: contentType, compliance_level: complianceLevel }
    });
    return response.data;
  }
};
